import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Box, Button, Card, CardContent, Menu, MenuItem, Skeleton, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import * as XLSX from 'xlsx'

import { useAppointmentsInRange } from '../api/analytics'

interface MonthlyRatingChartProps {
  env?: string
}

interface ChartData {
  month: string
  averageRating: number
  ratedCount: number
  appointmentCount: number
}

const formatPercent = (value: number): string => `${Math.round(value)}%`

const formatMonthLabel = (monthStr: string): string => {
  // monthStr is expected to be in format "YYYY-MM"
  const [year, month] = monthStr.split('-')
  const date = new Date(parseInt(year), parseInt(month) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

export const MonthlyRatingChart: React.FC<MonthlyRatingChartProps> = ({ env = 'test' }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // Default to last month
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth() - 1, 1) // First day of last month
  })
  const [toDate, setToDate] = useState<Date | null>(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 0) // Last day of last month
  })

  const fromDateStr = fromDate ? fromDate.toISOString() : ''
  const toDateStr = toDate ? toDate.toISOString() : ''

  // Ratings only exist for completed appointments
  const {
    data: appointmentsData,
    isLoading,
    error,
  } = useAppointmentsInRange(fromDateStr, toDateStr, 'scheduleStartTime', 'completed', env)

  const appointments = appointmentsData?.data

  const ratedAppointments = useMemo(
    () => (appointments ?? []).filter(appointment => appointment.rating != null),
    [appointments]
  )

  const chartData: ChartData[] = useMemo(() => {
    if (!appointments || appointments.length === 0) return []

    const monthlyData = new Map<
      string,
      { ratingSum: number; ratedCount: number; appointmentCount: number }
    >()

    appointments.forEach(appointment => {
      if (!appointment.scheduleStartTime) return

      const monthKey = format(new Date(appointment.scheduleStartTime), 'yyyy-MM')
      const existing = monthlyData.get(monthKey) ?? {
        ratingSum: 0,
        ratedCount: 0,
        appointmentCount: 0,
      }

      existing.appointmentCount += 1
      if (appointment.rating != null) {
        existing.ratingSum += appointment.rating
        existing.ratedCount += 1
      }
      monthlyData.set(monthKey, existing)
    })

    return Array.from(monthlyData.entries())
      .map(([month, data]) => ({
        month,
        averageRating: data.ratedCount > 0 ? data.ratingSum / data.ratedCount : 0,
        ratedCount: data.ratedCount,
        appointmentCount: data.appointmentCount,
      }))
      .sort((a, b) => a.month.localeCompare(b.month))
  }, [appointments])

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (exportFormat: 'csv' | 'xlsx') => {
    if (ratedAppointments.length === 0) return

    const exportData = ratedAppointments.map(appointment => ({
      'Appointment UUID': appointment.uuid,
      'Building UUID': appointment.buildingUUID,
      'Customer Account UUID': appointment.customerAccountUUID,
      'Scout UUID': appointment.scoutUUID || '',
      'Scout Name': appointment.scoutName || '',
      'Customer Name': appointment.customerName || '',
      'Customer Address': appointment.customerAddress || '',
      'Schedule Start Time': appointment.scheduleStartTime
        ? new Date(appointment.scheduleStartTime).toLocaleString('de-DE')
        : '',
      'Schedule End Time': appointment.scheduleEndTime
        ? new Date(appointment.scheduleEndTime).toLocaleString('de-DE')
        : '',
      Status: appointment.status,
      'Status Changed At': appointment.statusChangedAt
        ? new Date(appointment.statusChangedAt).toLocaleString('de-DE')
        : '',
      'Rating (%)': Math.round((appointment.rating ?? 0) * 100) / 100,
      Environment: appointment.environment,
      'Residential Units': appointment.residentialUnits,
      Type: appointment.scoutCreated ? 'Privat' : appointment.type,
      'Created At': new Date(appointment.createdAt).toLocaleString('de-DE'),
      'Updated At': new Date(appointment.updatedAt).toLocaleString('de-DE'),
    }))

    const ws = XLSX.utils.json_to_sheet(exportData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Rated Appointments')

    const dateRange = `${fromDate ? format(fromDate, 'yyyy-MM-dd') : 'start'}_${toDate ? format(toDate, 'yyyy-MM-dd') : 'end'}`

    XLSX.writeFile(wb, `appointments-rating-${dateRange}.${exportFormat}`)

    handleExportClose()
  }

  if (isLoading) {
    return (
      <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#2D3748',
              mb: 3,
            }}
          >
            Monthly Average Rating
          </Typography>
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: '8px' }} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={{ height: '100%', minHeight: 400, boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#2D3748',
                mb: 2,
              }}
            >
              Monthly Average Rating
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={newValue => setFromDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 180 },
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={newValue => setToDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 180 },
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {ratedAppointments.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 320,
            }}
          >
            <Typography color="error">Error loading monthly rating data</Typography>
          </Box>
        )}

        {!error && chartData.length > 0 && (
          <Box sx={{ width: '100%', height: 320, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 50,
                  bottom: 20,
                }}
              >
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#718096', fontSize: 11 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  tickFormatter={formatMonthLabel}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fill: '#718096' }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickLine={false}
                  domain={[0, 100]}
                  tickFormatter={formatPercent}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#2D3748', fontWeight: 600 }}
                  labelFormatter={formatMonthLabel}
                  formatter={(value: number, _name: string, item: { payload?: ChartData }) => {
                    const rated = item.payload?.ratedCount ?? 0
                    const total = item.payload?.appointmentCount ?? 0
                    return [
                      `${formatPercent(value)} (${rated} of ${total} rated)`,
                      'Average Rating',
                    ]
                  }}
                />
                <Bar dataKey="averageRating" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={60} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {!error && chartData.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 320,
            }}
          >
            <Typography color="textSecondary">No data available for the selected period</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default MonthlyRatingChart
